import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { setAccountId } from "@/lib/auth/account";
import { redeemLoginToken } from "@/lib/auth/redeemLoginToken";
import { fetchSession } from "@/lib/auth/session";
import { markHadSession, clearSignedOut } from "@/lib/auth/sessionMarker";
import { StudioApiError } from "@/types/api";

const REDEEM_STORAGE_PREFIX = "studio:login-token:redeem:";

function redeemStorageKey(loginToken: string): string {
  return `${REDEEM_STORAGE_PREFIX}${loginToken}`;
}

export function AuthEntryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loginToken = searchParams.get("login_token");
    const redirect = searchParams.get("redirect") || ROUTES.dashboard;
    const redirectPath = redirect.startsWith("/") ? redirect : `/${redirect}`;

    if (!loginToken) {
      setError("Missing login token. Open this page from the Stripe Dashboard app.");
      return;
    }

    const storageKey = redeemStorageKey(loginToken);

    const finish = (stripeAccountId: string | undefined) => {
      if (stripeAccountId?.startsWith("acct_")) {
        setAccountId(stripeAccountId);
        markHadSession();
        clearSignedOut();
      }
      navigate(redirectPath, { replace: true });
    };

    if (sessionStorage.getItem(storageKey) === "ok") {
      void fetchSession()
        .then((session) => finish(session?.stripe_account_id))
        .catch(() => finish(undefined));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const session = await redeemLoginToken(loginToken);
        sessionStorage.setItem(storageKey, "ok");
        finish(session.stripe_account_id);
      } catch (e) {
        if (cancelled) return;
        sessionStorage.removeItem(storageKey);
        if (e instanceof StudioApiError) {
          setError(`${e.message} (${e.code})`);
        } else {
          setError(e instanceof Error ? e.message : "Sign-in failed");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] p-6">
      <div className="max-w-md rounded-lg border border-[var(--border)] bg-white p-8 text-center shadow-sm">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-[var(--text)]">Sign-in failed</h1>
            <p className="mt-3 text-sm text-[var(--muted)]">{error}</p>
            <p className="mt-4 text-xs text-[var(--muted)]">
              Return to Stripe Dashboard, open InvoiceVault, and try again.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-[var(--text)]">Signing you in…</h1>
            <p className="mt-3 text-sm text-[var(--muted)]">
              Completing secure entry from Stripe Dashboard.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
